<template>
  <v-layout row wrap>
    <AuditItemDialog :dialogAuditItem.sync="dialogAuditItem" v-if="dialogAuditItem" />
    <SelectTemplateItemsDialog
      :templatedialog.sync="templatedialog"
      :currentAudit="currentAudit"
      v-if="templatedialog"
    />
    <AuditProcessContainer v-cloak>
      <GroupableItemList
        slot="container_content"
        :list-items="auditItems"
        list-config-key="auditPreperation"
        :manipulators="auditItemManipulators"
        :actions="listActions"
        @itemselected="templatedialog = true"
        @pdf-export="downloadPdf"
      >
        <template v-slot:default="{ item }">
          <AuditItemRow :key="item.id">
            <AuditQuestion
              slot="question"
              slot-scope="{ hover }"
              :hover="hover"
              :severity="item.question.importance"
              type="question"
            >
              <AuditQuestionRowComponent
                slot="content"
                :question="item.question"
                :standard="currentAudit.standard"
              />

              <AuditProcessMenu
                v-if="hover"
                slot-scope="{ hover }"
                slot="actions"
                :actions="getAuditItemActions"
                :context="{ref: {auditId, auditItemId: item.id}}"
                @itemdelete="deleteCurrentAuditItem(auditId, item.id)"
                @itemupdate="updateAuditItem(auditId, item.id, item)"
              />
            </AuditQuestion>
          </AuditItemRow>
        </template>
      </GroupableItemList>
    </AuditProcessContainer>

    <v-btn
      fab
      :style="fabStyle"
      bottom
      right
      color="pink"
      dark
      fixed
      @click="createAuditItem(auditId)"
    >
      <v-icon>add</v-icon>
    </v-btn>
  </v-layout>
</template>

<i18n>
{
  "de": {
    "importance": "Bedeutung",
    "audititem": "Auditfrage",
    "category": "Kategorie",
    "chapter" : "Kapitel",
    "use_template": "Vorlage nutzen",
    "pdf_export": "Frageliste drucken"
  },
  "en": {
    "importance": "Importance",
    "audititem": "Auditquestion",
    "category": "Category",
    "chapter" : "Chapter",
    "use_template": "Use Template",
    "pdf_export": "Print questionlist"
  }
}
</i18n>


<script>
// Dialogs
import AuditItemDialog from "@/components/dialogs/AuditItemDialog.vue";
import SelectTemplateItemsDialog from "@/components/dialogs/SelectTemplateItemsDialog.vue";

// Layouts
import AuditProcessContainer from "@/components/layouts/BaseLayouts/AuditProcessContainer.vue";

import AuditItemRow from "@/components/layouts/AuditPreparation/AuditItemRow.vue";
import AuditQuestion from "@/components/layouts/BaseLayouts/AuditQuestion.vue";
import GroupableItemList from "@/components/layouts/BaseLayouts/GroupableItemList";

// Controls/Snippets
import AuditProcessMenu from "@/components/controls/AuditProcessMenu.vue";

// Components
import AuditQuestionRowComponent from "@/components/widgets/AuditQuestionRowComponent.vue";

// Mixins
import FabMixin from "@/components/mixins/FabMixin.vue";
import AuditItemListMixin from "@/components/mixins/AuditItemListMixin";
import { formatDates } from "@/components/mixins/FormattingMixin";

import { mapGetters, mapActions } from "vuex";
import requestPdfReport from "@/utils/requestPdfReport";

import { api } from "@/store/modules/audit";
import { api as measuresApi } from "@/store/modules/audits";
import { api as appApi } from "@/store/modules/app";
import { api as configApi } from "@/store/modules/configuration";

import { chapter_cmp } from "@/utils/comperators";

export default {
  components: {
    AuditItemDialog,
    SelectTemplateItemsDialog,
    AuditProcessContainer,
    AuditItemRow,
    AuditQuestion,
    AuditProcessMenu,
    AuditQuestionRowComponent,
    GroupableItemList
  },
  mixins: [AuditItemListMixin, FabMixin],
  data() {
    return {
      dialogAuditItem: false,
      templatedialog: false,
      listActions: [
        {
          title: this.$i18n.t("use_template"),
          event: "itemselected"
        },
        {
          title: this.$i18n.t("pdf_export"),
          event: "pdf-export"
        }
      ]
    };
  },
  computed: {
    ...mapGetters(measuresApi.namespace, [
      measuresApi.getters.getFindingMeasureMapping,
      measuresApi.getters.getMeasuresByAuditId
    ]),
    ...mapGetters(api.namespace, [
      api.getters.getAuditProgress,
      api.getters.getAuditReportSummary,
      api.getters.getAuditStatistics
    ]),
    ...mapGetters(appApi.namespace, [appApi.getters.isLoading]),
    ...mapGetters(configApi.namespace, [
      configApi.getters.customerLogo,
      configApi.getters.getAuditItemActions
    ]),
    currentAuditReportSummary() {
      const result = this.getAuditReportSummary;
      return result;
    },

    pdfExportFilename() {
      return "Fragenliste_" + this.currentAudit.name + ".pdf";
    },
    pdfExportData() {
      if (this.currentAuditReportSummary === null) {
        return null;
      }
      const criterias = Object.entries(this.auditItems).map(
        ([id, audit_item]) => {
          return {
            auditCriteriaId: id,
            auditCriteriaNo: audit_item.no,
            auditCriteriaText: audit_item.question.text,
            auditCriteriaWeight: audit_item.Weight,
            auditCriteriaChapterIds: (audit_item.question.chapters || [])
              .map(chapter => chapter.id || null)
              .filter(v => typeof v === "string")
          };
        }
      );

      criterias.sort((lhs, rhs) => {
        const extract = chapters => {
          if (chapters.length) {
            chapters.sort();
            return chapters[0];
          } else {
            return "";
          }
        };
        lhs = extract(lhs.auditCriteriaChapterIds);
        rhs = extract(rhs.auditCriteriaChapterIds);

        return chapter_cmp(lhs, rhs);
      });
      const data = {
        auditUrl: window.location.href,
        auditId: this.auditId,
        auditName: this.currentAudit.name,
        customerLogo: this.customerLogo,
        exportDate: this.moment().format("l"),
        auditPlaningYear: this.currentAudit.planning_year,
        auditingDates: formatDates(this.currentAudit.auditing_date, null),
        auditStandards: this.auditStandard,
        auditType: this.currentAudit.type,
        auditBy: this.currentAudit.audited_by,
        leadAuditor: this.currentAuditReportSummary.leadAuditor,
        responsibleForMeasures: this.currentAuditReportSummary.responsibleUser,
        coAuditors: this.currentAuditReportSummary.auditCoAuditors, // ["Markus Meier", "Erna Etna"],
        participants: this.currentAuditReportSummary.auditParticipants, // this.currentAudit.members, ["Markus Meier", "Erna Etna", "Frowin Zaun"],
        auditSummary: this.currentAudit.summary,

        statistics: this.getAuditStatistics.statistics,
        criterias
      };
      console.log("pdfExportData", data);
      return data;
    },
    pdfGenerator() {
      return requestPdfReport(this.pdfExportData, "preparation");
    }
  },
  methods: {
    ...mapActions(appApi.namespace, [
      appApi.actions.addStatus,
      appApi.actions.setLoading
    ]),
    createAuditItem(audit_id) {
      this.setDialogItem({ question: { chapters: [] } });
      this.setDialogAction("create");
      this.setAuditId(audit_id);

      this.dialogAuditItem = true;
    },
    updateAuditItem(audit_id, audititem_id, audititem) {
      this.setAuditId(audit_id);
      this.setAuditItemId(audititem_id);
      this.setDialogItem({ ...audititem });
      this.setDialogAction("update");

      this.dialogAuditItem = true;
    },
    deleteCurrentAuditItem(audit_id, audititem_id) {
      const payload = { audit_id: audit_id, audititem_id: audititem_id };

      if (confirm("Are you sure you want to delete this audit item?")) {
        this.deleteAuditItem(payload);
      }
    },
    downloadPdf() {
      this.setLoading(true);
      const self = this;
      this.pdfGenerator()
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          self.downloadBlob = blob;
          self.blobUrl = url;

          const a = document.createElement("a");
          document.body.appendChild(a);
          a.href = self.blobUrl;
          a.download = self.pdfExportFilename;
          a.click();
          document.body.removeChild(a);
          this.setLoading(false);
        })
        .catch(err => {
          console.log("Download failed", err);
          self.addStatus({ message: "Download failed", type: "error" });
          this.setLoading(false);
        });
    }
  }
};
</script>
